import { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, RefreshCw, Loader2, Eye, EyeOff, Upload, ArrowUp, ArrowDown, Pencil, DollarSign, ImageOff, Save } from "lucide-react";

const CATS = ["corporativo","esportivo","camisetas","polos","moletons","jaquetas","personalizados"];

export default function Produtos() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({ name:"", category:"corporativo", description:"", fabrics:"", techniques:"", min_quantity:15, image:"", price:"", show_price:false });
  const uploadRef = useRef({}); // per-product file input refs

  const load = () => api.get("/products").then(({data}) => setItems(data));
  useEffect(() => { load(); }, []);

  const syncTiny = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/tiny/sync/products");
      if (data.already_running) { toast.info("Sincronização já em andamento"); setSyncing(false); return; }
      toast.info("Sincronizando produtos do Tiny...");
      const poll = setInterval(async () => {
        const { data: st } = await api.get("/integrations/tiny/status");
        if (!st.sync?.running) {
          clearInterval(poll); setSyncing(false); load();
          if (st.sync?.error) toast.error(`Erro: ${st.sync.error}`);
          else toast.success(`${st.counts?.products_synced || 0} produtos no catálogo`);
        }
      }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao sincronizar"); setSyncing(false); }
  };

  const openNew = () => { setEditing(null); setForm({ name:"", category:"corporativo", description:"", fabrics:"", techniques:"", min_quantity:15, image:"", price:"", show_price:false }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "", category: p.category || "corporativo",
      description: p.description || "",
      fabrics: (p.fabrics || []).join(", "), techniques: (p.techniques || []).join(", "),
      min_quantity: p.min_quantity || 15, image: p.image || "",
      price: p.price != null ? String(p.price) : "", show_price: !!p.show_price,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    const body = {
      name: form.name, category: form.category, description: form.description,
      fabrics: form.fabrics.split(",").map((s)=>s.trim()).filter(Boolean),
      techniques: form.techniques.split(",").map((s)=>s.trim()).filter(Boolean),
      min_quantity: Number(form.min_quantity)||15,
      image: form.image || "",
      price: form.price === "" ? null : Number(form.price),
      show_price: !!form.show_price,
    };
    try {
      if (editing) { await api.patch(`/products/${editing}`, body); toast.success("Produto atualizado"); }
      else { await api.post("/products", body); toast.success("Produto criado"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remover este produto?")) return;
    try { await api.delete(`/products/${id}`); load(); } catch(e) { toast.error("Sem permissão"); }
  };
  const toggleSite = async (p) => {
    try { await api.patch(`/products/${p.id}`, { visible_on_site: p.visible_on_site === false }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sem permissão"); }
  };
  const togglePrice = async (p) => {
    try { await api.patch(`/products/${p.id}`, { show_price: !p.show_price }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sem permissão"); }
  };
  const uploadImage = async (p, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/products/${p.id}/image`, fd); toast.success("Foto atualizada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha no upload"); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items]; [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr); // optimistic
    try { await api.post("/products/reorder", { order: arr.map((p) => p.id) }); }
    catch (e) { toast.error("Falha ao reordenar"); load(); }
  };
  const brl = (v) => "R$ " + (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Produtos</h1>
          <p className="text-slate-400 text-sm mt-1">Catálogo da ATP — reordene, edite fotos e escolha se o preço aparece no site.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={syncTiny} disabled={syncing} variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10" data-testid="prod-sync-tiny">
            {syncing ? <Loader2 size={14} className="animate-spin mr-2"/> : <RefreshCw size={14} className="mr-2"/>}Sincronizar Tiny
          </Button>
          <Button onClick={openNew} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="prod-new-btn"><Plus size={16} className="mr-2"/>Novo produto</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p, idx) => (
          <div key={p.id} className="bg-[#111622] border border-[#1E293B] rounded-xl overflow-hidden flex flex-col" data-testid={`prod-card-${p.id}`}>
            <div className="aspect-video overflow-hidden bg-[#0B0F17] relative group">
              {p.image
                ? <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-slate-600"><ImageOff size={28}/></div>}
              <input ref={(el)=>uploadRef.current[p.id]=el} type="file" accept="image/*" className="hidden"
                onChange={(e)=>{ const f=e.target.files?.[0]; e.target.value=""; uploadImage(p, f); }} data-testid={`prod-upload-input-${p.id}`}/>
              <Button size="sm" onClick={()=>uploadRef.current[p.id]?.click()} className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white h-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`prod-upload-${p.id}`}>
                <Upload size={12} className="mr-1"/>{p.image ? "Trocar" : "Enviar"}
              </Button>
              <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" onClick={()=>move(idx,-1)} disabled={idx===0} className="h-7 w-7 p-0 bg-black/70 hover:bg-black text-white" data-testid={`prod-up-${p.id}`}><ArrowUp size={12}/></Button>
                <Button size="sm" onClick={()=>move(idx, 1)} disabled={idx===items.length-1} className="h-7 w-7 p-0 bg-black/70 hover:bg-black text-white" data-testid={`prod-down-${p.id}`}><ArrowDown size={12}/></Button>
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-xs text-[#0CDBF2] font-mono uppercase tracking-widest">{p.category}</div>
              <div className="font-heading text-lg font-semibold mt-1">{p.name}</div>
              {p.sku && <div className="text-[10px] text-slate-500 font-mono">SKU: {p.sku}</div>}
              <div className="text-sm text-slate-400 mt-1 line-clamp-2">{p.description}</div>
              {p.price != null && Number(p.price) > 0 && (
                <div className={`text-sm font-mono mt-1 flex items-center gap-1 ${p.show_price ? "text-[#0CDBF2]" : "text-slate-500"}`} data-testid={`prod-price-${p.id}`}>
                  <DollarSign size={12}/>{brl(p.price)}{!p.show_price && <span className="text-[10px] text-slate-600">(oculto no site)</span>}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {p.techniques?.map((t) => <Badge key={t} variant="outline" className="border-slate-700 text-slate-300 text-[10px]">{t}</Badge>)}
              </div>
              <div className="text-xs text-slate-500 mt-2">Mín. {p.min_quantity} · {p.fabrics?.join(", ")}</div>

              <div className="mt-3 space-y-2">
                <label className="flex items-center justify-between text-xs bg-[#0B0F17] rounded-lg px-3 py-2 cursor-pointer" data-testid={`prod-showprice-row-${p.id}`}>
                  <span className="text-slate-300">Mostrar valor no site</span>
                  <Switch checked={!!p.show_price} onCheckedChange={()=>togglePrice(p)} data-testid={`prod-showprice-${p.id}`}/>
                </label>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={()=>toggleSite(p)} data-testid={`prod-site-${p.id}`}
                    className={`flex-1 ${p.visible_on_site === false ? "border-slate-700 text-slate-400" : "border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10"}`}>
                    {p.visible_on_site === false ? <><EyeOff size={12} className="mr-1"/>Oculto</> : <><Eye size={12} className="mr-1"/>Visível</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={()=>openEdit(p)} className="border-slate-700" data-testid={`prod-edit-${p.id}`}><Pencil size={12}/></Button>
                  <Button size="sm" variant="outline" onClick={()=>del(p.id)} className="border-slate-700" data-testid={`prod-del-${p.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300 text-sm">Nome*</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-name"/></div>
            <div><Label className="text-slate-300 text-sm">Categoria</Label>
              <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} data-testid="prod-category"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                {CATS.map((c)=><option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><Label className="text-slate-300 text-sm">Descrição</Label><Textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-desc"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-sm">Tecidos (vírgula)</Label><Input value={form.fabrics} onChange={(e)=>setForm({...form,fabrics:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-fabrics" placeholder="Dry Liso, Algodão"/></div>
              <div><Label className="text-slate-300 text-sm">Técnicas (vírgula)</Label><Input value={form.techniques} onChange={(e)=>setForm({...form,techniques:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-tech" placeholder="Sublimação, DTF"/></div>
              <div><Label className="text-slate-300 text-sm">Quantidade mín.</Label><Input type="number" value={form.min_quantity} onChange={(e)=>setForm({...form,min_quantity:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-minq"/></div>
              <div><Label className="text-slate-300 text-sm">Preço (R$)</Label><Input type="number" step="0.01" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-price"/></div>
              <div className="col-span-2"><Label className="text-slate-300 text-sm">Imagem (URL)</Label><Input value={form.image} onChange={(e)=>setForm({...form,image:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="prod-image" placeholder="/api/products/.../image/raw ou URL externa"/></div>
              <div className="col-span-2 flex items-center gap-2 bg-[#111622] rounded-lg p-3">
                <Switch checked={!!form.show_price} onCheckedChange={(v)=>setForm({...form, show_price:v})} data-testid="prod-form-showprice"/>
                <span className="text-sm">Mostrar valor no site</span>
              </div>
            </div>
            <Button onClick={save} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="prod-save"><Save size={14} className="mr-2"/>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
